import { Button, Flex } from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import { SettingsIcon } from "@chakra-ui/icons";
import { NavLink, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import {
	type Ticket,
	TicketPriority,
	TicketStatus,
} from "../types/ticket.types";
import { useCenterTickets, useTickets } from "../hooks/tickets.hooks";
import UserAvatar from "../components/UserAvatar";
import { format } from "date-fns";
import { useTicketLabel, useTicketLabels } from "../hooks/ticket_labels.hooks";
import DataTable, { SearchBar } from "../components/table/ServerSideTable";
import TicketStatusBadge from "../components/TicketStatusBadge";
import TicketPriorityBadge from "../components/TicketPriorityBadge";
import Select from "../components/inputs/Select";
import { useConnectedUser } from "../hooks/user.hooks";
import { useCenters } from "../hooks/centers.hooks";
import { useDataTableContext } from "../hooks/data-table.hooks";
import { getLongExposedId } from "../services/command.service";

const TicketLabelBadge = ({ id }: { id: number | string }) => {
	const ticketLabel = useTicketLabel(id);

	if (!ticketLabel) {
		return <>---</>;
	}
	return (
		<div className="px-1.5 py-0.5 w-fit text-xs font-normal leading-4 bg-gray-50 text-gray-700 border! border-red-700 rounded">
			{ticketLabel.label}
		</div>
	);
};

const TicketingTableActionComponent = () => {
	const { filters, setFilters } = useDataTableContext();
	const { data: reasons } = useTicketLabels({});

	return (
		<Flex gap={4}>
			{reasons && (
				<Select
					placeholder="Motif"
					value={filters && filters.reason}
					onChange={(evt) =>
						setFilters && setFilters("reason", evt.target.value)
					}
				>
					{reasons.map((reason) => (
						<option key={reason.id} value={reason.id}>
							{reason.label}
						</option>
					))}
				</Select>
			)}
			<Select
				placeholder="Statut"
				value={filters && filters.status}
				onChange={(evt) => setFilters && setFilters("status", evt.target.value)}
			>
				<option value={TicketStatus.CLOSED}>Fermé</option>
				<option value={TicketStatus.OPEN}>Ouvert</option>
			</Select>
			<Select
				placeholder="Priorité"
				value={filters && filters.priority}
				onChange={(evt) =>
					setFilters && setFilters("priority", evt.target.value)
				}
			>
				<option value={TicketPriority.LOW}>Normale</option>
				<option value={TicketPriority.MEDIUM}>Moyenne</option>
				<option value={TicketPriority.HIGH}>Haute</option>
			</Select>
		</Flex>
	);
};

function TicketingPage() {
	const navigate = useNavigate();
	const user = useConnectedUser();
	const { center } = useCenters();
	const columns = useMemo<ColumnDef<Ticket>[]>(
		() => [
			{
				accessorFn: (row) => row.createdAt,
				id: "id",
				header: "ID",
				cell: ({ row }) => (
					<div className="text-xs font-medium text-gray-900">
						{format(row.original.createdAt, "yyyy")}-{row.id}
					</div>
				),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.ticketLabelId,
				id: "ticket_label_id",
				header: "Motif",
				cell: ({ row }) => <TicketLabelBadge id={row.original.ticketLabelId} />,
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.status,
				id: "status",
				header: "Statut",
				cell: ({ row }) => (
					<TicketStatusBadge id={row.original.id} editable={false} />
				),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.createdAt,
				id: "createdAt",
				header: "Création",
				cell: ({ row }) => (
					<div className="text-xs font-medium text-gray-900">
						{format(row.original.createdAt, "dd/MM/yyyy")}
					</div>
				),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => {
					if (!row.openById) {
						return <></>;
					}
					return <UserAvatar userId={row.openById}></UserAvatar>;
				},
				id: "owner_id",
				header: "Ouvert par",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.commandId,
				id: "command_id",
				header: "Lié à",
				cell: ({ row }) => {
					if (row.original.commandId) {
						return (
							<>
								<NavLink to={`/orders/${row.original.commandId}`}>
									<div className="text-xs font-medium text-brand-700">
										{row.original.command
											? getLongExposedId(row.original.command)
											: "---"}
									</div>
								</NavLink>
							</>
						);
					}
					return <>---</>;
				},
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.priority,
				id: "priority",
				header: "Priorité",
				cell: ({ row }) => (
					<TicketPriorityBadge priority={row.original.priority} />
				),
				sortDescFirst: true,
			},
		],
		[],
	);

	if (user?.isSuperAdmin === false && center === null) {
		return;
	}
	return (
		<>
			<Flex justifyContent={"space-between"}>
				<PageTitle>Ticketing</PageTitle>
				<Flex gap="4">
					{user?.isSuperAdmin && (
						<Button
							w="fit-content"
							variant={"outline"}
							onClick={() => navigate("/tickets/labels")}
						>
							<SettingsIcon mr={4}></SettingsIcon>Gérer les libellés
						</Button>
					)}
				</Flex>
			</Flex>
			<DataTable
				columns={columns}
				paginatedQuery={
					user?.isSuperAdmin
						? useTickets
						: // eslint-disable-next-line react-hooks/rules-of-hooks
							useCenterTickets({
								id: center ? center.id : Number.NaN,
							})
				}
				defaultSortBy={[
					{
						id: "created_at",
						desc: true,
					},
				]}
				w={["100px", "200px", "100px", "100px", "200px", "100px", "100px"]}
				flexGrow={[1, 1, 1, 1, 4, 1, 1]}
				toolbar={
					<Flex my="1rem" justifyContent="space-between">
						<SearchBar />
						<TicketingTableActionComponent />
					</Flex>
				}
				cellLink={(cell) => {
					if (cell.id.endsWith("linkedTo")) {
						return;
					}
					return `/tickets/${cell.row.id}`;
				}}
			></DataTable>
		</>
	);
}

export default TicketingPage;
